import { v4 as uuid } from "uuid";

import {
  issue__DocumentAnalysisStatus,
  issue__EventType,
  Participant,
} from "@medbillai/graphql-types";

import type {
  BaseEvent,
  ChatEvent,
  DocumentAnalysisEvent,
  DocumentUploadedEvent,
  UserSelectionRequestedEvent,
} from "./types";

type EventOptions = {
  sentAt?: string;
};

const createSentByProfile = (participant: Participant) => ({
  __typename: "user__Profile_Type" as const,
  name: participant === Participant.USER ? "You" : null,
});

type BaseEventFields = Pick<
  BaseEvent,
  | "id"
  | "eventType"
  | "participant"
  | "sentAt"
  | "userVisible"
  | "eventFeedbacks"
  | "sentByProfile"
>;

const createBaseEvent = <T extends BaseEvent["__typename"]>({
  __typename,
  eventType,
  participant,
  sentAt,
}: {
  __typename: T;
  eventType: issue__EventType;
  participant: Participant;
  sentAt?: string;
}): BaseEventFields & { __typename: T } => ({
  __typename,
  id: uuid().toString(),
  eventType,
  participant,
  sentAt: sentAt ?? new Date().toISOString(),
  userVisible: true,
  eventFeedbacks: [],
  sentByProfile: createSentByProfile(participant),
});

/**
 * Create a user chat event for preview cases with all required fields
 */
export function createUserChatEvent(
  message: string,
  options: EventOptions = {},
): ChatEvent {
  return {
    ...createBaseEvent({
      __typename: "issue__ChatEvent_Type",
      eventType: issue__EventType.CHAT,
      participant: Participant.USER,
      sentAt: options.sentAt,
    }),
    chatBody: message,
  };
}

/**
 * Create a system message event for preview cases with all required fields.
 * If options are provided, creates a UserSelectionRequestedEvent.
 * Otherwise, creates a regular chat event.
 */
export function createSystemMessageEvent(
  message: string,
  options?: string[],
  eventOptions: EventOptions = {},
): UserSelectionRequestedEvent | ChatEvent {
  if (options && options.length > 0) {
    return {
      ...createBaseEvent({
        __typename: "issue__UserSelectionRequestedEvent_Type",
        eventType: issue__EventType.USER_SELECTION_REQUESTED,
        participant: Participant.SERVICE,
        sentAt: eventOptions.sentAt,
      }),
      stringMessage: message,
      stringOptions: options,
    };
  }

  return {
    ...createBaseEvent({
      __typename: "issue__ChatEvent_Type",
      eventType: issue__EventType.CHAT,
      participant: Participant.SERVICE,
      sentAt: eventOptions.sentAt,
    }),
    chatBody: message,
  };
}

/**
 * Create a document analysis event for preview cases with all required fields.
 * Defaults to PENDING status to represent an in-progress analysis.
 */
export function createDocumentAnalysisEvent(
  status: issue__DocumentAnalysisStatus = issue__DocumentAnalysisStatus.PENDING,
): DocumentAnalysisEvent {
  return {
    __typename: "issue__DocumentAnalysisEvent_Type" as const,
    id: uuid().toString(),
    eventType: issue__EventType.DOCUMENT_ANALYSIS,
    participant: Participant.SERVICE,
    sentAt: new Date().toISOString(),
    userVisible: true,
    eventFeedbacks: [],
    sentByProfile: {
      __typename: "user__Profile_Type" as const,
      name: null,
    },
    status,
    errorMessage: null,
    errorIsRetriable: null,
  };
}

/**
 * Create a document uploaded event for preview cases with all required fields
 */
export function createDocumentUploadEvent(
  {
    id,
    uploading,
    sentAt,
  }: {
    id?: string;
    uploading?: boolean;
    sentAt?: string;
  } = {
    uploading: false,
  },
): DocumentUploadedEvent {
  return {
    ...createBaseEvent({
      __typename: "issue__DocumentUploadedEvent_Type",
      eventType: issue__EventType.DOCUMENT_UPLOADED,
      participant: Participant.USER,
      sentAt,
    }),
    isClusterEnd: false,
    ...(id ? { id } : {}),
    uploading,
    uploadFailed: false,
    document: {
      __typename: "document__Document_Type" as const,
      id: uuid().toString(),
      name: "document.pdf",
      createdAt: new Date().toISOString(),
      files: [],
      dataType: null,
      latestIngestion: null,
    },
  };
}
